import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft,
  Users, 
  Calendar, 
  AlertCircle, 
  Edit,
  Shield,
  Clock,
  Trophy,
  Target,
  TrendingUp,
  FileText,
  Building2,
  ChevronRight
} from 'lucide-react';
import { api } from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface Team {
  id: string;
  name: string;
  ageGroup: string;
  season: string;
  description?: string;
  coachId: string;
  assessmentInterval: number;
  createdAt: string;
  updatedAt: string;
  logoUrl?: string;
  collaboratingCoach?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  pendingInvitation?: {
    email: string;
    firstName: string;
    lastName: string;
    expiresAt: string;
  } | null;
  assistantCoachName?: string;
  assistantCoachRole?: string;
  trainingSessions?: {
    id: string;
    dayOfWeek: string;
    location: string;
    startTime: string;
    endTime: string;
    surfaceType: string;
  }[];
}

interface Player {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  position?: string;
  teamId: string;
}

export const TeamDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [team, setTeam] = useState<Team | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      fetchTeamDetails();
      fetchPlayers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTeamDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/teams/${id}`);
      setTeam(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load team details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await api.get(`/teams/${id}/players`);
      setPlayers(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to load players:', err);
    }
  };

  if (loading && !team) {
    return <LoadingSpinner />;
  }

  if (error && !team) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!team) {
    return null;
  }

  const isOwner = team.coachId === user?.id;

  // Mock data for stats (will be replaced with real data later)
  const stats = {
    players: players.length,
    matchesPlayed: 0,
    wins: 0,
    goalsScored: 0
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <Button
        variant="ghost"
        onClick={() => navigate('/teams')}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Teams
      </Button>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Team Header */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              {/* Team Logo */}
              {team.logoUrl ? (
                <img 
                  src={team.logoUrl} 
                  alt={`${team.name} logo`}
                  className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-20 h-20 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-10 h-10 text-blue-600" />
                </div>
              )}
              
              {/* Team Info */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{team.name}</h1>
                <p className="text-gray-600 mb-4">{team.ageGroup} • Season {team.season}</p>
                
                {/* Coach Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Head Coach</p>
                    <p className="font-medium">{user?.profile.firstName} {user?.profile.lastName}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">
                      {team.assistantCoachRole || (team.collaboratingCoach ? 'Co-Coach' : 'Assistant Coach')}
                    </p>
                    {team.collaboratingCoach ? (
                      <p className="font-medium">{team.collaboratingCoach.firstName} {team.collaboratingCoach.lastName}</p>
                    ) : team.assistantCoachName ? (
                      <p className="font-medium">{team.assistantCoachName}</p>
                    ) : team.pendingInvitation ? (
                      <p className="font-medium text-amber-500">{team.pendingInvitation.firstName} {team.pendingInvitation.lastName} (Pending)</p>
                    ) : (
                      <p className="font-medium text-gray-400">Not assigned</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {isOwner && (
              <Button
                variant="outline"
                onClick={() => navigate(`/teams/${id}/edit`)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Details
              </Button>
            )}
          </div>

          {/* Team Details */}
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-start gap-2 text-sm">
              <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
              <div>
                <p className="text-gray-500">Training Schedule</p>
                {team.trainingSessions && team.trainingSessions.length > 0 ? (
                  <div className="space-y-1">
                    {team.trainingSessions.map((s) => (
                      <p key={s.id} className="font-medium">
                        {s.dayOfWeek} {s.startTime}–{s.endTime}
                        {s.location && <span className="text-gray-400 font-normal"> · {s.location}</span>}
                        {s.surfaceType && <span className="text-gray-400 font-normal"> · {s.surfaceType}</span>}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="font-medium text-gray-400">Not scheduled</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.players}</p>
                <p className="text-sm text-gray-600">Players</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.matchesPlayed}</p>
                <p className="text-sm text-gray-600">Matches Played</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Trophy className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.wins}</p>
                <p className="text-sm text-gray-600">Wins</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.goalsScored}</p>
                <p className="text-sm text-gray-600">Goals Scored</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upcoming Fixtures */}
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Fixtures</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No upcoming fixtures scheduled</p>
                <Button variant="outline" size="sm" className="mt-4">
                  Add Fixture
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Training Schedule */}
          <Card>
            <CardHeader>
              <CardTitle>Training Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No training sessions scheduled</p>
                <Button variant="outline" size="sm" className="mt-4">
                  Add Training Session
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Players List */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Squad</CardTitle>
                <Button
                  size="sm"
                  onClick={() => navigate('/players')}
                >
                  View All Players
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {players.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No players added yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => navigate('/players')}
                  >
                    Add Players
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {players.slice(0, 5).map((player) => (
                    <div
                      key={player.id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/players/${player.id}`)}
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {player.firstName} {player.lastName}
                        </p>
                        {player.position && (
                          <p className="text-sm text-gray-500">{player.position}</p>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  ))}
                  {players.length > 5 && (
                    <Button
                      variant="ghost"
                      className="w-full mt-2"
                      onClick={() => navigate('/players')}
                    >
                      View all {players.length} players
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Suggested Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Suggested Actions</CardTitle>
              <CardDescription>Personalized recommendations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                   onClick={() => navigate('/assess')}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm">Complete Player Assessments</p>
                      <Badge variant="destructive" className="text-xs">High Priority</Badge>
                    </div>
                    <p className="text-xs text-gray-600">Regular assessments help track development progress.</p>
                  </div>
                </div>
              </div>

              <div className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                   onClick={() => navigate('/develop')}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm">Review Development Cycle</p>
                      <Badge variant="secondary" className="text-xs">Medium</Badge>
                    </div>
                    <p className="text-xs text-gray-600">Update your training focus areas.</p>
                  </div>
                </div>
              </div>

              <div className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                   onClick={() => navigate('/reports')}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm">Share Progress Reports</p>
                      <Badge variant="secondary" className="text-xs">Medium</Badge>
                    </div>
                    <p className="text-xs text-gray-600">Keep parents informed of player progress.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Links */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/players')}
              >
                <Users className="w-4 h-4 mr-2" />
                View All Players
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/reports/team/${id}`)}
              >
                <FileText className="w-4 h-4 mr-2" />
                Team Report
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/develop')}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Development Cycle
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/club')}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Club Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
